import { useLoading } from "@/Contexts/LoadingContext";
import { usePage } from "@inertiajs/inertia-react";
import { useAtom } from "jotai/index";
import {
  dateFilterAtom,
  generalFilterAtom,
  locationFilterAtom,
  questionFilterAtom,
  teamFilterAtom,
  dashboardTagFilterAtom,
  dashboardSortAtom,
} from "@/Stores";
import { formatDate } from "@/Utilities/Utilities";
import { Inertia } from "@inertiajs/inertia";
import {
  ChevronDown,
  ChevronUp,
  Download,
  Filter,
  MoreVertical,
} from "react-feather";
import { ActionMenu } from "@/Components/ActionMenu";
import {
  LabeledMultiSelectAutocomplete,
  LabeledSelect,
} from "@/Components/Form";
import { DateSelectSpan, Subheader } from "@/Components";
import { IPCC_FORMAT } from "@/Enums/ipcc_format";
import { useEffect, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import Tooltip from "@/Components/Tooltip";
import ShrinkableDashboardFilterItems from "@/Components/ShrinkableDashboardFilterItems";
import { isValid } from "date-fns";

const DashboardFilter = ({ isSticky = false }) => {
  const { setIsLoaded } = useLoading();

  const [renderKey, setRenderKey] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);

  const teams = usePage().props?.teams?.data || [];
  const questions = usePage().props?.questions?.data || [];
  const locations =
    usePage().props?.locations?.map((location) => ({
      value: location?.location_place_id || "",
      label: location?.location || "",
    })) || [];

  const tags = questions
    .map((question) => question.tags)
    .flat()
    .filter((v, i, a) => a.findIndex((t) => t.id === v.id) === i)
    .sort((a, b) => a.name.localeCompare(b.name));

  tags.unshift({ id: -1, name: "Uncategorized" });

  const [teamFilter, setTeamFilter] = useAtom(teamFilterAtom);
  const [questionFilter, setQuestionFilter] = useAtom(questionFilterAtom);
  const [dateFilter, setDateFilter] = useAtom(dateFilterAtom);
  const [locationFilter, setLocationFilter] = useAtom(locationFilterAtom);
  const [generalFilter, setGeneralFilter] = useAtom(generalFilterAtom);
  const [tagFilter, setTagFilter] = useAtom(dashboardTagFilterAtom);
  const sort = useAtomValue(dashboardSortAtom);

  const [filtersDirty, setFiltersDirty] = useState(false);

  const params = new URLSearchParams(window.location.search);

  const getFilterValue = (paramName, defaultValue, isDateFilter = false) => {
    if (!isDateFilter) {
      return params.has(paramName)
        ? params
            .get(paramName)
            .split(",")
            .map((v) => (isNaN(v) ? v : Number(v)))
        : defaultValue;
    }

    return params.has(`${paramName}[from]`)
      ? {
          from: params.get(`${paramName}[from]`),
          to: params.get(`${paramName}[to]`),
        }
      : defaultValue;
  };

  const LoadingState = {
    PRELOADED: 0,
    PENDING: 1,
    COMPLETED: 2,
  };

  const [tempFilters, setTempFilters] = useState({
    teamFilter: getFilterValue(
      "teamFilter",
      teamFilter !== null ? teamFilter : teams.map((team) => team.id),
    ),
    questionFilter: getFilterValue(
      "questionFilter",
      questionFilter !== null
        ? questionFilter
        : questions.map((question) => question.id),
    ),
    dateFilter: getFilterValue("dateFilter", dateFilter, true),
    locationFilter: getFilterValue(
      "locationFilter",
      locationFilter !== null
        ? locationFilter
        : locations.map((location) => location.value),
    ),
    tagFilter: getFilterValue(
      "tagFilter",
      tagFilter !== null ? tagFilter : tags.map((tag) => tag.id),
    ),
    generalFilter: params.has("generalFilter[format][]")
      ? {
          reports: params.get("generalFilter[reports]"),
          format: params.get("generalFilter[format][]"),
          source: params.get("generalFilter[source][]") || [],
        }
      : generalFilter,
    initialLoad: params.has("dateFilter[from]")
      ? LoadingState.PRELOADED
      : LoadingState.PENDING,
  });

  const saveFilter = (filters) => {
    setTeamFilter(filters.teamFilter);
    setQuestionFilter(filters.questionFilter);
    setDateFilter(filters.dateFilter);
    setLocationFilter(filters.locationFilter);
    setGeneralFilter(filters.generalFilter);
    setTagFilter(filters.tagFilter);
    setFiltersDirty(false);
    setIsLoaded(true);
    setRenderKey((prev) => prev + 1);
  };

  useEffect(() => {
    if (tempFilters.initialLoad === LoadingState.PRELOADED) {
      // Set the atom to the preloaded values

      saveFilter(tempFilters);

      setTempFilters((prev) => ({
        ...prev,
        loadingState: LoadingState.COMPLETED,
      }));
    } else if (tempFilters.initialLoad === LoadingState.PENDING) {
      updateFilters();
      setTempFilters((prev) => ({
        ...prev,
        loadingState: LoadingState.COMPLETED,
      }));
    }
  }, []);

  const isFirstSortRender = useRef(true);

  useEffect(() => {
    if (isFirstSortRender.current) {
      // This is the initial render, set the ref to false and skip the effect to avoid infinite loop
      isFirstSortRender.current = false;
      return;
    }

    updateFilters();
  }, [sort]);

  const urlParamsPayload = {
    teamFilter: tempFilters?.teamFilter ? tempFilters.teamFilter.join(",") : [],
    questionFilter: tempFilters?.questionFilter
      ? tempFilters.questionFilter.join(",")
      : [],
    locationFilter: tempFilters?.locationFilter
      ? tempFilters.locationFilter.join(",")
      : [],
    tagFilter: tempFilters?.tagFilter ? tempFilters.tagFilter.join(",") : [],
    generalFilter: tempFilters.generalFilter,
    dateFilter: {
      from: "",
      to: "",
    },
    sort: sort,
  };

  Object.keys(tempFilters.dateFilter).forEach((key) => {
    // Make sure the dates is formatted correctly and is in the swedish timezone
    urlParamsPayload.dateFilter[key] = formatDate(
      new Date(tempFilters.dateFilter[key]).toISOString(),
      "yyyy-MM-dd",
    );
  });

  const updateFilters = () => {
    Inertia.visit(route(route()?.current(), urlParamsPayload), {
      only: [
        "answers",
        "teams",
        "answers_order",
        "aggregated_scientific_uncertainty",
      ],
      replace: false,
      preserveState: true,
      preserveScroll: true,
      onSuccess: () => {
        saveFilter(tempFilters);
      },
    });
  };

  function handleChangeFilter(val, filter) {
    // console.log('handleChangeFilter', val, filter);
    setFiltersDirty(true);
    setTempFilters((oldFilters) => {
      const updatedFilters = { ...oldFilters };
      switch (filter) {
        case "team":
          updatedFilters.teamFilter = val.map((v) => v.value);
          break;
        case "question":
          updatedFilters.questionFilter = val.map((v) => v.value);
          break;
        case "date":
          updatedFilters.dateFilter = val;
          break;
        case "location":
          updatedFilters.locationFilter = val.map((v) => v.value);
          break;
        case "tag":
          updatedFilters.tagFilter = val.map((v) => v.value);
          break;
        default:
          updatedFilters.generalFilter = {
            ...oldFilters.generalFilter,
            [filter]: Array.isArray(val)
              ? val.map((v) => v.value)
              : val?.target?.value || null,
          };
          break;
      }
      return updatedFilters;
    });
  }

  const prevIsSticky = useRef(isSticky);

  useEffect(() => {
    if (prevIsSticky.current === false && isSticky === true) {
      setIsMinimized(true);
    }

    if (
      prevIsSticky.current === true &&
      isSticky === false &&
      isMinimized === true
    ) {
      setIsMinimized(false);
    }

    prevIsSticky.current = isSticky;
  }, [isSticky]);

  const actionMenuItems = [
    {
      label: "Download report",
      icon: <Download />,
      callback: () => {
        const link = document.createElement("a");
        link.href = route("dashboard.export", urlParamsPayload);
        link.download = "";
        link.click();
      },
    },
  ];

  const actionMenuRef = useRef(null);

  return (
    <>
      <div
        key={renderKey}
        className={`rounded-md bg-primary px-8 4xl:mx-0 4xl:max-w-screen-3xl ${
          isMinimized ? "py-4" : "py-8"
        }`}
      >
        <header className="flex justify-between">
          {!isMinimized && (
            <Subheader
              className="mb-8 mt-0 tracking-widest text-white"
              margin="mt-0"
            >
              FILTERS
            </Subheader>
            // <h2 className="mb-8 text-md font-bold text-white">Filters</h2>
          )}
          {isMinimized && (
            /**
             * Show filters but minimized and with dropdown to expand
             */
            <div className="flex max-w-[calc(100%_-_165px)] grow items-center gap-8 pr-8 text-white">
              <Filter className="shrink-0 text-current" />
              <ShrinkableDashboardFilterItems
                items={[
                  {
                    html: (
                      <p>
                        {isValid(new Date(dateFilter.from))
                          ? formatDate(new Date(dateFilter.from).toISOString())
                          : "From date"}
                        &nbsp;-&nbsp;
                        {isValid(new Date(dateFilter.to))
                          ? formatDate(new Date(dateFilter.to).toISOString())
                          : "To date"}
                      </p>
                    ),
                    name: "Reporting period",
                  },
                  {
                    html: (
                      <FilterHoverTooltip
                        items={
                          teamFilter
                            ? teamFilter.map(
                                (teamId) =>
                                  teams?.find((team) => team.id === teamId)
                                    ?.name,
                              )
                            : []
                        }
                      >
                        <p className="cursor-help">
                          Teams&nbsp;({teamFilter.length || 0})
                        </p>
                      </FilterHoverTooltip>
                    ),
                    name: `Teams (${teamFilter.length || 0})`,
                  },
                  {
                    html: (
                      <FilterHoverTooltip
                        items={
                          questionFilter
                            ? questionFilter.map(
                                (questionId) =>
                                  questions?.find(
                                    (question) => question.id === questionId,
                                  )?.title,
                              )
                            : []
                        }
                      >
                        <p className="cursor-help">
                          Activities&nbsp;({questionFilter.length || 0})
                        </p>
                      </FilterHoverTooltip>
                    ),
                    name: `Activities (${questionFilter.length || 0})`,
                  },
                  {
                    html: (
                      <FilterHoverTooltip
                        items={
                          tagFilter
                            ? tagFilter.map(
                                (tagId) =>
                                  tags?.find((tag) => tag.id === tagId)?.name,
                              )
                            : []
                        }
                      >
                        <p className="cursor-help">
                          Categories and modules&nbsp;(
                          {tagFilter.length || 0})
                        </p>
                      </FilterHoverTooltip>
                    ),
                    name: `Categories and modules (${tagFilter.length || 0})`,
                  },
                  {
                    html: (
                      <FilterHoverTooltip
                        items={
                          locationFilter
                            ? locationFilter.map(
                                (locationId) =>
                                  locations?.find(
                                    (location) => location.value === locationId,
                                  )?.label,
                              )
                            : []
                        }
                      >
                        <p className="cursor-help">
                          Locations&nbsp;({locationFilter.length || 0})
                        </p>
                      </FilterHoverTooltip>
                    ),
                    name: `Locations (${locationFilter.length || 0})`,
                  },
                ]}
              />
            </div>
          )}

          <div className="flex items-start gap-4">
            {isSticky && (
              <a
                href="#filter"
                className="btn-white-outline shrink-0 grow-0 sm:hidden"
              >
                Edit filters
              </a>
            )}
            <button
              onClick={() => setIsMinimized((prev) => !prev)}
              className={`${
                isMinimized ? "btn-white-outline shrink-0 grow-0" : ""
              } ${isSticky ? "hidden sm:block" : ""}`}
            >
              {isMinimized ? (
                <div className="flex gap-2">
                  Edit filters
                  <ChevronDown size={24} className="text-current" />
                </div>
              ) : (
                <ChevronUp size={24} className="text-white" />
              )}
            </button>
            <div
              className={`hover-circle relative z-10 ml-2 ${
                isMinimized ? "mt-2" : "mt-0"
              }`}
              onClick={() => {
                actionMenuRef.current.toggleMenu();
              }}
            >
              <MoreVertical
                size={24}
                className="shrink-0 cursor-pointer text-white"
              />
              <ActionMenu
                ref={actionMenuRef}
                items={actionMenuItems}
                theme="light"
                className="shadow-sm"
              />
            </div>
          </div>
        </header>

        {!isMinimized && (
          <>
            <div className="grid grid-cols-1 gap-x-2 align-middle text-white sm:grid-cols-2 lg:grid-cols-3">
              <DateSelectSpan
                label="Reporting period"
                className="mb-4 mt-0 min-h-[46px] w-full rounded-none px-2 text-gray-700 shadow-sm"
                containerClass={"text-gray-700"}
                labelClass={"text-white"}
                value={dateFilter}
                buttons={[
                  "last12months",
                  "month",
                  "year",
                  "lastMonth",
                  "lastYear",
                ]}
                onChange={(val) => handleChangeFilter(val, "date")}
              />

              <LabeledMultiSelectAutocomplete
                title="Teams"
                className="mb-4"
                options={teams.map((team) => ({
                  value: team.id,
                  label: team.name,
                }))}
                initialChecked={true}
                defaultValue={teamFilter}
                placeholder={`Teams`}
                onSelect={(val) => handleChangeFilter(val, "team")}
              />

              <LabeledMultiSelectAutocomplete
                title="Activities"
                className="mb-4"
                options={questions
                  .map((question) => ({
                    value: question.id,
                    label: question.title,
                  }))
                  .sort((a, b) => a.label.localeCompare(b.label))}
                defaultValue={questionFilter}
                placeholder={`Activities`}
                onSelect={(val) => handleChangeFilter(val, "question")}
              />

              <LabeledMultiSelectAutocomplete
                title="Categories and modules"
                className="mb-4"
                options={tags.map((tag) => ({
                  value: tag.id,
                  label: tag.name,
                }))}
                defaultValue={tagFilter}
                placeholder={`Tags`}
                onSelect={(val) => handleChangeFilter(val, "tag")}
              />

              <LabeledMultiSelectAutocomplete
                title="Locations"
                options={locations}
                defaultValue={tempFilters.locationFilter}
                onSelect={(val) => handleChangeFilter(val, "location")}
                placeholder={`Locations`}
              />

              <LabeledMultiSelectAutocomplete
                title="Data sources"
                initialChecked={true}
                options={[
                  { label: "Reporters", value: "reporters" },
                  { label: "Calculators", value: "calculators" },
                  { label: "Webhooks", value: "webhooks" },
                ]}
                defaultValue={tempFilters.generalFilter?.source || []}
                onSelect={(val) => handleChangeFilter(val, "source")}
                placeholder={`Sources`}
              />

              <LabeledSelect
                title="IPCC report format"
                options={IPCC_FORMAT}
                defaultValue={tempFilters.generalFilter?.format || "ar5"}
                onChange={(val) => handleChangeFilter(val, "format")}
                placeholder={`Formats`}
                noDefault={true}
              />
            </div>

            <div className="mt-8 flex justify-start gap-2 align-middle text-white">
              <label className="mr-4">
                <input
                  type="radio"
                  name="reports"
                  onChange={(e) => handleChangeFilter(e, "reports")}
                  value="all"
                  checked={
                    (tempFilters.generalFilter?.reports || "all") === "all"
                  }
                />
                <span className="ml-2">All reports</span>
              </label>
              <label className="mr-4">
                <input
                  type="radio"
                  name="reports"
                  onChange={(e) => handleChangeFilter(e, "reports")}
                  value="my_reports"
                  checked={
                    (tempFilters.generalFilter?.reports || "all") ===
                    "my_reports"
                  }
                />
                <span className="ml-2">My reports</span>
              </label>
            </div>

            <div className="flex justify-end">
              <button
                className="rounded-full bg-yellow px-6 py-3 text-black disabled:bg-gray-400 disabled:text-white max-sm:mt-8"
                disabled={!filtersDirty}
                onClick={() => updateFilters()}
              >
                Apply filter
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

const FilterHoverTooltip = ({ children, items }) => {
  const Text = () => {
    return (
      <div className="flex flex-col">
        {items.map((item, i) => {
          return (
            <div key={i} className="flex gap-2">
              <span>{item}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Tooltip tooltipText={<Text />} position="bottom">
      {children}
    </Tooltip>
  );
};

export default DashboardFilter;
